import type {EntityCustomFieldType} from 'nutshell-graphql-types';
import type {
    EntityAttributeValue as EntityAttributeValueFragment,
    CustomFieldValueInput,
} from 'nutshell-graphql-types';

/**
 * Utility function to take a (nullable) EntityAttributeValue and a fieldType
 * and turn that into a paylaod that our GraphQL updateCustomFieldValue
 * mutation expects.
 *
 * @param  {Object} [value]     Full value object for the field (is nullable)
 * @param  {string} fieldType   The type of field. This information is redundant
 *                              with the value’s type, but that value might not
 *                              exist so we need to include it here
 * @return {Object}             Value object we can pass to our mutation
 */
export function getCustomFieldValueForMutation(
    value: EntityAttributeValueFragment | null | undefined,
    fieldType: EntityCustomFieldType
): CustomFieldValueInput {
    if (value) {
        return getNonNullValueForMutation(value);
    } else {
        return getNullValueForMutation(fieldType);
    }
}

function getNonNullValueForMutation(value: EntityAttributeValueFragment) {
    if (value.__typename === 'EntityAttributeValueString') {
        return {
            string: value.value,
        };
    } else if (value.__typename === 'EntityAttributeValueText') {
        return {
            text: value.value,
        };
    } else if (value.__typename === 'EntityAttributeValueEnum') {
        return {
            enumSingle: value.value,
        };
    } else if (value.__typename === 'EntityAttributeValueDate') {
        return {
            date: new Date(value.date * 1000).toISOString(),
        };
    } else if (value.__typename === 'EntityAttributeValueset') {
        return {
            enumMultiple: value.all
                .filter((entityAttributeValueEnum) => {
                    return entityAttributeValueEnum.__typename === 'EntityAttributeValueEnum';
                })
                .map((entityAttributeValueEnum) => {
                    return entityAttributeValueEnum.value;
                }),
        };
    } else {
        return {
            location: null,
        };
    }
}

function getNullValueForMutation(fieldType: EntityCustomFieldType) {
    switch (fieldType) {
        case 'STRING':
            return {
                string: null,
            };
        case 'TEXT':
            return {
                text: null,
            };
        case 'ENUM':
            return {
                enumSingle: null,
            };
        case 'DATE':
            return {
                date: null,
            };
        default:
            return {
                location: null,
            };
    }
}
